import type { Entity } from '@backstage/catalog-model';

export const JOB_GROUPS = [
  'Unknown',
  'Contractor',
  'Engineering',
  'Product/Design',
  'Customer Support',
  'Management',
  'Sales/Marketing',
  'Operations/Field',
  'Finance/Legal/HR',
  'Other',
] as const;

export type JobGroup = (typeof JOB_GROUPS)[number];

/** Properties for mixpanel.people.set. A key is absent when there is no value. */
export type JobProfileProperties = {
  job_title?: string;
  job_title_normalized?: string;
  job_group?: JobGroup;
  organization?: string;
  department?: string;
};

/** Workday placeholder department that is never sent. */
export const PLACEHOLDER_DEPARTMENT = 'Temporary_Holding Family';

// JS `\b` is ASCII-only and does not fire next to accented letters
// (e.g. "qualité"), so word boundaries are explicit Unicode lookarounds.
const WORD_START = '(?<![\\p{L}\\p{N}])';
const WORD_END = '(?![\\p{L}\\p{N}])';

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const wholeWord = (pattern: string) =>
  new RegExp(`${WORD_START}${pattern}${WORD_END}`, 'gu');

const TITLE_REPLACEMENTS: Array<[RegExp, string]> = [
  [wholeWord('s\\/w'), 'software'],
  [wholeWord('h\\/w'), 'hardware'],
  [wholeWord('sr\\.?'), 'senior'],
  [wholeWord('jr\\.?'), 'junior'],
  [wholeWord('front[\\s/-]*end'), 'frontend'],
  [wholeWord('back[\\s/-]*end'), 'backend'],
  [wholeWord('full[\\s/-]*stack'), 'fullstack'],
  [wholeWord('dev[\\s/-]*ops'), 'devops'],
  [wholeWord('sub[\\s-]*contractor'), 'subcontractor'],
  [wholeWord('sous[\\s-]*traitant'), 'sous-traitant'],
];

/**
 * Normalises a job title with general rules (no role tables).
 * Returns undefined when nothing is left after normalisation.
 */
export function normalizeJobTitle(raw: string): string | undefined {
  let title = raw.trim().toLowerCase();

  for (const [pattern, replacement] of TITLE_REPLACEMENTS) {
    title = title.replace(pattern, replacement);
  }

  title = title
    .replace(/[.,;:!?()[\]{}"*_|]/g, ' ')
    .replace(/\s+[-/]+\s+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-/&+]+|[\s\-/&+]+$/g, '');

  return title || undefined;
}

const GROUP_KEYWORDS: Array<[JobGroup, string[]]> = [
  [
    'Unknown',
    [
      'profile to determinate',
      'placeholder',
      'holding profile',
      'to be determined',
      'tbd',
    ],
  ],
  [
    'Contractor',
    [
      'subcontractor',
      'contractor',
      'outsourced',
      'sous-traitant',
      'alternant',
      'interim*',
      'intérim*',
      'freelance*',
      'offshore',
      'apprenti',
      'stagiaire',
      'intern',
      'internship',
      'working student',
    ],
  ],
  [
    'Engineering',
    [
      'engineer*',
      'ingénieur*',
      'developer*',
      'developper*',
      'developpeur*',
      'développeur*',
      'dev lead',
      'tech lead',
      'technical lead*',
      'leader technique',
      'programmer',
      'programming',
      'software',
      'frontend',
      'backend',
      'fullstack',
      'devops',
      'sre',
      'reliability',
      'architect*',
      'qa',
      'quality assurance',
      'test',
      'tester',
      'testeur',
      'valideur',
      'pentester',
      'analyst',
      'data scientist',
      'security',
      'it',
      'system administrator',
      'systems administrator',
    ],
  ],
  ['Product/Design', ['product', 'design*', 'ux', 'ui', 'researcher']],
  [
    'Customer Support',
    [
      'customer',
      'client',
      'support',
      'service desk',
      'helpdesk',
      'help desk',
      'member services',
      'partner success',
      'care',
    ],
  ],
  [
    'Management',
    [
      'manager',
      'managing',
      'head of',
      'director',
      'directeur',
      'directrice',
      'chief',
      'vp',
      'vice president',
      'ceo',
      'cto',
      'cfo',
      'coo',
      'team lead*',
      'squad lead*',
      'responsable',
      'chef de projet*',
      'supervisor',
      'coordinator',
      'coordinateur',
      'coordinatrice',
      'leiter*',
    ],
  ],
  [
    'Sales/Marketing',
    [
      'sales',
      'presales',
      'pre-sales',
      'account',
      'marketing',
      'business development',
      'bid',
      'crm',
      'commercial*',
      'communication*',
      'brand',
      'growth',
      'partnership*',
      'vente*',
    ],
  ],
  [
    'Operations/Field',
    [
      'technician',
      'technicien*',
      'techniker',
      'field',
      'opérateur',
      'opératrice',
      'operator',
      'operations',
      'logistique',
      'logistics',
      'warehouse',
      'maintenance',
      'workshop',
      'assembl*',
      'production',
      'supply chain',
      'purchas*',
      'buyer',
      'acheteur*',
      'quality',
      'qualité',
      'industrialisation',
      'install*',
      'driver',
      'magasinier*',
      'repair*',
      'réparation*',
      'wartung*',
    ],
  ],
  [
    'Finance/Legal/HR',
    [
      'financ*',
      'accountant*',
      'accounting',
      'accounts payable',
      'controller',
      'controlling',
      'contrôleur de gestion',
      'controleur de gestion',
      'comptab*',
      'treasur*',
      'tax',
      'audit*',
      'payroll',
      'paie',
      'legal',
      'lawyer',
      'counsel',
      'jurist*',
      'juriste',
      'compliance',
      'hr',
      'human resources',
      'people',
      'talent',
      'recruit*',
      'learning & development',
      'l&d',
      'admin*',
      'assistant*',
      'office',
      'procurement',
    ],
  ],
];

// One RegExp per group, compiled once at module load. Array order is the
// rule priority: the first group that matches wins.
const GROUP_RULES: Array<[JobGroup, RegExp]> = GROUP_KEYWORDS.map(
  ([group, keywords]) => {
    const alternatives = keywords.map(keyword =>
      keyword.endsWith('*')
        ? `${escapeRegExp(keyword.slice(0, -1))}[\\p{L}\\p{N}]*`
        : escapeRegExp(keyword),
    );
    return [
      group,
      new RegExp(`${WORD_START}(?:${alternatives.join('|')})${WORD_END}`, 'u'),
    ];
  },
);

/**
 * Coarse group by keywords. Expects an already normalised title
 * (the result of normalizeJobTitle). Returns 'Other' when no rule matches.
 */
export function getJobGroup(normalizedTitle: string): JobGroup {
  const rule = GROUP_RULES.find(([, pattern]) => pattern.test(normalizedTitle));
  return rule ? rule[0] : 'Other';
}

function readAnnotation(entity: Entity, key: string): string | undefined {
  const value = entity.metadata.annotations?.[key];
  if (typeof value !== 'string') {
    return undefined;
  }
  return value.trim() || undefined;
}

/**
 * Builds Mixpanel profile properties from User entity annotations.
 * An undefined entity (not in the catalog) yields {}.
 */
export function buildJobProfileProperties(
  entity: Entity | undefined,
): JobProfileProperties {
  if (!entity) {
    return {};
  }

  const properties: JobProfileProperties = {};

  const role = readAnnotation(entity, 'user/role');
  if (role) {
    properties.job_title = role;
    const normalized = normalizeJobTitle(role);
    if (normalized) {
      properties.job_title_normalized = normalized;
      properties.job_group = getJobGroup(normalized);
    }
  }

  const organization = readAnnotation(entity, 'user/organization');
  if (organization) {
    properties.organization = organization;
  }

  const department = readAnnotation(entity, 'user/department');
  if (
    department &&
    department.toLowerCase() !== PLACEHOLDER_DEPARTMENT.toLowerCase()
  ) {
    properties.department = department;
  }

  return properties;
}
